import { useEffect, useRef } from 'react';

/** Peak/RMS bar driven straight off an AnalyserNode. */
export function Meter({
  analyser,
  vertical = false,
  className,
}: {
  analyser: AnalyserNode | null;
  vertical?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const peak = useRef(0);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf = 0;
    const data = new Float32Array(analyser ? analyser.fftSize : 256);

    const draw = () => {
      raf = requestAnimationFrame(draw);
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth * dpr;
      const h = canvas.clientHeight * dpr;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      let rms = 0;
      if (analyser) {
        analyser.getFloatTimeDomainData(data);
        for (let i = 0; i < data.length; i++) rms += data[i] * data[i];
        rms = Math.sqrt(rms / data.length);
      }
      const db = rms > 0 ? 20 * Math.log10(rms) : -100;
      const level = Math.max(0, Math.min(1, (db + 60) / 60));
      peak.current = Math.max(level, peak.current * 0.93);

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#161b20';
      ctx.fillRect(0, 0, w, h);

      const grad = ctx.createLinearGradient(0, vertical ? h : 0, vertical ? 0 : w, 0);
      grad.addColorStop(0, '#8b5cf6');
      grad.addColorStop(0.7, '#fbbf24');
      grad.addColorStop(1, '#ff2d95');
      ctx.fillStyle = grad;
      if (vertical) ctx.fillRect(0, h - level * h, w, level * h);
      else ctx.fillRect(0, 0, level * w, h);

      ctx.fillStyle = '#e6ebef';
      if (vertical) ctx.fillRect(0, h - peak.current * h - 1 * dpr, w, 1.5 * dpr);
      else ctx.fillRect(peak.current * w - 1.5 * dpr, 0, 1.5 * dpr, h);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [analyser, vertical]);

  return <canvas ref={ref} className={className} />;
}
